/**
 * DOM 오버레이 UI — HUD, 피드(연대기 스트림), 씬 라벨, 뒤로가기, 필지 메뉴, 시즌 종료 오버레이.
 * 3D 안에 UI를 그리지 않는다(지시서 4). sim 상태를 읽어 갱신만.
 */
import type { PlayerView, WorldView } from '@villager/shared';
import type { SeasonResult, World } from '@villager/sim-core';

function $(id: string): HTMLElement {
  const e = document.getElementById(id);
  if (!e) throw new Error(`#${id} 없음`);
  return e;
}

export class UI {
  private sGold = $('s-gold');
  private sSales = $('s-sales');
  private sFront = $('s-front');
  private sNet = $('s-net');
  private sAdv = $('s-adv');
  private sDead = $('s-dead');
  private raidWarn = $('raid-warn');
  private feedEl = $('feed');
  private label = $('scene-label');
  private backbtn = $('backbtn') as HTMLButtonElement;
  private menu = $('menu');
  private overlay = $('overlay');
  private ovT = $('ov-t');
  private ovB = $('ov-b');
  private lastFeedId = -1;

  refreshHUD(w: World): void {
    this.sGold.textContent = String(Math.floor(w.gold));
    this.sSales.textContent = String(w.sales);
    this.sFront.textContent = `${w.liberationPct}%`;
    this.sNet.textContent = String(w.netProfit);
    this.sNet.style.color = w.netProfit >= 0 ? '#e8b34b' : '#ff8a6a';
    this.sAdv.textContent = String(w.adventurerCount);
    this.sDead.textContent = String(w.deadCount);
  }

  /** 네트워크 모드 HUD — 권위 서버 스냅샷 기준. */
  refreshHUDNet(you: PlayerView, world: WorldView): void {
    this.sGold.textContent = String(you.gold);
    this.sSales.textContent = String(you.sales);
    this.sFront.textContent = `${world.liberationPct}%`;
    this.sNet.textContent = String(you.netProfit);
    this.sNet.style.color = you.netProfit >= 0 ? '#e8b34b' : '#ff8a6a';
    this.sAdv.textContent = String(world.adventurers);
    this.sDead.textContent = String(world.dead);
  }

  /** 접속 모드 표시 (멀티/싱글). */
  setMode(text: string, online: boolean): void {
    let el = document.getElementById('mode-chip');
    if (!el) {
      el = document.createElement('div');
      el.id = 'mode-chip';
      el.className = 'chip';
      document.getElementById('hud')?.appendChild(el);
    }
    el.innerHTML = `${online ? '🌐' : '🔒'} <b>${text}</b>`;
  }

  /** 습격 예고 배너 (싱글). */
  renderRaidWarning(w: World): void {
    this.renderRaidWarningAt(w.raidWarning);
  }

  /** 습격 예고 배너 — 남은 초(없으면 null). */
  renderRaidWarningAt(t: number | null): void {
    if (t === null) {
      this.raidWarn.style.display = 'none';
      return;
    }
    this.raidWarn.style.display = 'block';
    this.raidWarn.innerHTML = `⚠ 마왕군 습격대 접근 — <b>${Math.max(0, t).toFixed(1)}초</b> 후 필지 타격 · 망루가 막아낸다`;
  }

  /** 서버 피드 항목을 그대로 렌더 (네트워크 모드). */
  pushFeed(msgs: readonly { id: number; msg: string; red: boolean }[]): void {
    for (const m of msgs) {
      if (m.id <= this.lastFeedId) continue;
      this.lastFeedId = m.id;
      const d = document.createElement('div');
      d.innerHTML = m.red ? `<span class="r">${m.msg}</span>` : m.msg;
      this.feedEl.appendChild(d);
      while (this.feedEl.children.length > 7) this.feedEl.firstChild?.remove();
    }
  }

  renderFeed(w: World): void {
    for (const m of w.feedMessages) {
      if (m.id <= this.lastFeedId) continue;
      this.lastFeedId = m.id;
      const d = document.createElement('div');
      d.innerHTML = m.red ? `<span class="r">${m.msg}</span>` : m.msg;
      this.feedEl.appendChild(d);
      while (this.feedEl.children.length > 7) this.feedEl.firstChild?.remove();
    }
  }

  setLabel(html: string): void {
    this.label.innerHTML = html;
  }

  showBack(show: boolean): void {
    this.backbtn.style.display = show ? 'block' : 'none';
  }

  onBack(cb: () => void): void {
    this.backbtn.onclick = cb;
  }

  menuButton(label: string, cost: string, onClick: () => void): HTMLButtonElement {
    const b = document.createElement('button');
    const s1 = document.createElement('span');
    s1.innerHTML = label;
    const s2 = document.createElement('span');
    if (cost) {
      s2.className = 'c';
      s2.textContent = cost;
    }
    b.append(s1, s2);
    b.addEventListener('click', onClick);
    return b;
  }

  openMenu(x: number, y: number, title: string, desc: string, buttons: HTMLButtonElement[]): void {
    this.menu.innerHTML = '';
    const h = document.createElement('h3');
    h.innerHTML = title;
    this.menu.appendChild(h);
    if (desc) {
      const d = document.createElement('div');
      d.className = 'desc';
      d.innerHTML = desc;
      this.menu.appendChild(d);
    }
    for (const b of buttons) this.menu.appendChild(b);
    this.menu.style.display = 'block';
    this.menu.style.left = `${Math.min(x, window.innerWidth - 265)}px`;
    this.menu.style.top = `${Math.min(y, window.innerHeight - 320)}px`;
  }

  closeMenu(): void {
    this.menu.style.display = 'none';
  }

  get menuOpen(): boolean {
    return this.menu.style.display === 'block';
  }

  /** 시즌 결산 = 개인 연대기 요약 (지시서 M3). */
  showOverlay(res: SeasonResult): void {
    const G = ['F', 'E', 'D', 'C', 'B', 'A', 'S'];
    this.ovT.textContent = `용사 등극 — ${res.heroName}`;

    const patrons = res.topPatrons.length
      ? res.topPatrons
          .map(
            (p) =>
              `<div style="margin:2px 0">· <b>${p.name}</b> (${G[p.grade]}급) — ${p.visits}회 방문, ${p.spent}금</div>`,
          )
          .join('')
      : '<div style="opacity:.6">· 아직 단골이 없었다</div>';

    // 이 게임의 핵심 감정: 용사가 내 단골이었는가
    const heroLine = res.heroWasPatron
      ? `<div style="margin:10px 0;color:#e8b34b;font-size:15px">★ 용사는 당신의 <b>단골</b>이었다 — ${res.heroVisits}번 찾아와 ${res.heroSpent}금을 썼다.</div>`
      : `<div style="margin:10px 0;opacity:.75">용사는 끝내 당신의 가게에 들르지 않았다. — 아직은.</div>`;

    this.ovB.innerHTML = `
      <div>${G[res.heroGrade]}급 모험가 <b>${res.heroName}</b>이(가) 마왕을 쓰러뜨렸다.</div>
      <div style="opacity:.75;margin-top:4px">시즌 ${Math.round(res.seasonSeconds)}초 · 스러진 모험가 ${res.dead}명 · 묘비 ${res.tombs}기</div>
      ${heroLine}
      <div style="text-align:left;display:inline-block;margin-top:6px;font-size:13px;line-height:1.7">
        <div style="color:#e8b34b;margin-bottom:4px">■ 내 단골</div>
        ${patrons}
        <div style="color:#e8b34b;margin:10px 0 4px">■ 장부</div>
        <div>· 필지 ${res.parcels}곳 · 매출 <b>${res.sales}</b>금</div>
        <div>· 세금·유지비 ${res.taxPaid}금 · 시장수수료 ${res.feesPaid}금</div>
        <div>· 순이익 <b>${res.netProfit}</b>금</div>
      </div>
      <div style="margin-top:12px;opacity:.6">기록은 남았다. 다음 시즌에도 그들은 당신의 가게를 지나갈 것이다.</div>`;
    this.overlay.style.display = 'flex';
  }
}
